import { useMatch, useNavigate, useParams } from "react-router-dom";
import { Card, Dialog, DialogContent, IconButton, Stack } from "@mui/material";

import { Form, FormikProvider, useFormik } from "formik";
import {
  ProductListColumns,
  comapnyInfoDefaultValues,
  taxBillingDefaultValues,
  thirdPartySettingsDefaultValues,
  advancedSettingsDefaultValue,
  productCatalogDefaultValue,
  whitelabelInfoDefaultValues,
  InternationalizationDefaultValues,
  COMMONTEXT,
  communicationDefaultValue,
  payoutScheduleDefaultValues,
} from "./company.constants";
import {
  companyinfoValidationSchema,
  whitelabelInfoValidationSchema,
  taxBillingConfigValidationSchema,
  thirdPartyValidationSchema,
  communicationValidationSchema,
  payoutScheduleValidationSchema,
} from "src/util/validationSchemas";
import { LoadingButton } from "@mui/lab";
import {
  companyUrl,
  getCompanyUrlId,
  getPaymentGateway,
  getCompanyProxyPdf,
  WEB_APP_ENDPOINT,
} from "src/react-query/endPoints";
import {
  useGetDetail,
  useGetPaymentGatewayList,
  useList,
  useMutate,
} from "src/hooks/useFetch.hook";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loader } from "../Loader";
import { is } from "src/util/is";
import { alertSlice } from "src/store/alert/alert.slice";
import { useDispatch } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { issueImgUpload } from "../ServiceRequest/serviceRequest.hook";
import { fetchApi, fetchWithError } from "src/react-query/fetchWithError";
import { selector } from "src/react-query/select";
import { queryKeys } from "src/react-query/queryKeys";
import { fileToBase64 } from "src/utils/fileConvertBase64";
import { GridCloseIcon } from "@mui/x-data-grid";
import ImgPreviewModal from "../ImagePreviewModal/ImgPreviewModal";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import sanitizeValues from "src/utils/sanitizeValues";
import enums, { FEATURE } from "src/util/enums";
import {
  isValidImageType,
  MAX_IMAGE_SIZE_MB,
  isValidFileType,
  MAX_FILE_SIZE_MB,
} from "src/utils/fileTypes";
import CommonAccordion from "../CommonAccordion/CommonAccordion";
import CompanyFeatureEntitlements from "./CompanyFeatureEntitlements";
import { useDropdown } from "src/hooks/useDropdown.hook";
import { BaseTable } from "../BaseTable/BaseTable";
import { useAccess } from "src/hooks/useAccess.hook";

const CompanyBasicInfoSection = React.lazy(() =>
  import("./CompanyBasicInfoSection")
);
const CompanyPaymentConfigSection = React.lazy(() =>
  import("./CompanyPaymentConfigSection")
);
const CompanyWhitelabelSection = React.lazy(() =>
  import("./CompanyWhitelabelSection")
);
const CompanyAddressSection = React.lazy(() =>
  import("./CompanyAddressSection")
);
const CompanyInfoAndCommentsSection = React.lazy(() =>
  import("./CompanyInfoAndCommentsSection")
);
const AdvanceSettingsSection = React.lazy(() =>
  import("./AdvanceSettingsSection")
);
const CompanyInternationation = React.lazy(() =>
  import("./CompanyInternationation")
);
const CompanyFinancialSettingsSection = React.lazy(() =>
  import("./CompanyFinancialSettingsSection")
);
const Communicate = React.lazy(() => import("./Communicate"));
const PayoutScheduleSection = React.lazy(() =>
  import("./PayoutScheduleSection")
);

export const CompanyForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    actions: { setAlert },
  } = alertSlice;
  const { companyId } = useParams();
  const { data: currencies } = useList("getCurrencies");
  const { data: smsGatewayList } = useList("getSmsGateway");
  const { data: TaxRegimeType } = useDropdown("TaxRegimeType");
  const { data: TaxIDFieldLabel } = useDropdown("TaxIDFieldLabel");
  const { data: InvoiceNumberSchema } = useDropdown("InvoiceNumberSchema");
  const { data: localeCode } = useDropdown("LocaleCode");
  const { data: timeZone } = useDropdown("TimeZone");
  const { data: dateFormat } = useDropdown("DateFormat");
  const { data: numberFormat } = useDropdown("NumberFormat");
  const { data: addressFormat } = useDropdown("AddressFormat");
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutate("companyList");
  const {
    mutate: availablePaymentGatewayList,
    isLoading: isFetchingAvailablePaymentGatewayList,
  } = useGetPaymentGatewayList();
  const isCreateCompany = useMatch("/company/new");
  const { data: company, isFetching } = useGetDetail("company", {
    id: companyId,
  });
  const [gatewayList, setGatewayList] = useState([]);
  const [imageUploadPending, setImageUploadPending] = useState(false);
  const imageUploadInputRef = useRef();
  const mapMarkerActiveInputRef = useRef();
  const mapMarkerInUseInputRef = useRef();
  const mapMarkerInactiveInputRef = useRef();
  const termsNconditionUrl = useRef();
  const imgUpload = useMutation(issueImgUpload);
  const [photoModal, setPhotoModal] = useState({
    open: false,
    src: "",
    type: "",
  });
  const [tnCdata, setTnCdata] = useState();
  const [isUploadingCompanyLogo, setIsUploadingCompanyLogo] = useState(false);
  const [isUploadingMapMarkerActive, setIsUploadingMapMarkerActive] =
    useState(false);
  const [isUploadingMapMarkerInUse, setIsUploadingMapMarkerInUse] =
    useState(false);
  const [isUploadingMapMarkerInactive, setIsUploadingMapMarkerInactive] =
    useState(false);
  const [isUploadingTermsNcondition, setIsUploadingTermsNcondition] =
    useState(false);
  const [taxLabel, setTaxLabel] = useState("Tax");
  const { data: product, isFetching: isFetchingProduct } = useList(
    "companyproductCatalogUrl"
  );
  const [rows, setRows] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState("companyInfo");
  const isReadOnly = useAccess(FEATURE.COMPANY, "R");
  const canEdit = !isReadOnly;

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const onCompnayInfoSubmit = async (companyInfoData, { setSubmitting }) => {
    const sanitizeData = sanitizeValues(companyInfoData);
    const {
      street,
      houseNumber,
      zipCode,
      city,
      state,
      country,
      locationLatitude,
      locationLongitude,
      addressId,
      paymentGatewayId,
    } = sanitizeData;

    sanitizeData["charger_configuration_url"] = `${sanitizeData?.protocol}${sanitizeData?.charger_configuration_url}`

    if (paymentGatewayId === 0) {
      let errorsUpd = {
        ...errorsComapnyInfo,
        paymentGatewayId: "Please select a payment gateway",
      };
      setErrorsComapnyInfo(errorsUpd);
      dispatch(
        setAlert({
          open: true,
          message: "Please select a payment gateway",
          severity: "error",
        })
      );
      setSubmitting(false);
      return;
    }

    sanitizeData.address = {
      street,
      houseNumber,
      zipCode,
      city,
      state,
      country,
      lat: locationLatitude,
      lng: locationLongitude,
    };

    const mutateObject = {
      url: companyUrl,
      method: "POST",
    };

    if (!isCreateCompany && !is.nullOrUndefined(companyId)) {
      mutateObject.method = "PUT";
      mutateObject.url = getCompanyUrlId(companyId);
      sanitizeData.address.addressId = addressId;
      delete sanitizeData.addressId;
    }
    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { companyInfo: sanitizeData },
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const formikComapnyInfo = useFormik({
    initialValues: comapnyInfoDefaultValues(),
    validationSchema: companyinfoValidationSchema,
    onSubmit: onCompnayInfoSubmit,
  });

  const {
    errors: errorsComapnyInfo,
    touched: touchedComapnyInfo,
    values: valuesComapnyInfo,
    handleSubmit: handleSubmitComapnyInfo,
    isSubmitting: isSubmittingComapnyInfo,
    getFieldProps: getFieldPropsComapnyInfo,
    resetForm: resetFormComapnyInfo,
    setFieldValue: setFieldValueComapnyInfo,
    setErrors: setErrorsComapnyInfo,
  } = formikComapnyInfo;
  
  const onTaxBillingSubmit = async (taxBillingData, { setSubmitting }) => {
    const sanitizeData = sanitizeValues(taxBillingData);
    const mutateObject = {};

    if (!isCreateCompany && !is.nullOrUndefined(companyId)) {
      mutateObject.method = "PUT";
      mutateObject.url = getCompanyUrlId(companyId);
    } else {
      dispatch(
        setAlert({
          open: true,
          message: `${COMMONTEXT.REGISTRATION_REQUIRED_MESSAGE} Financial Settings`,
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { taxBillingConfig: sanitizeData.taxBillingConfig }, // key change here
      },
      { onSuccess: onUpdatedSuccess }
    );

    setSubmitting(false);
  };

  const companyCountry = valuesComapnyInfo?.country;
  const companyNameForInvoice = valuesComapnyInfo?.name;

  const taxBillingSchema = useMemo(
    () => taxBillingConfigValidationSchema(companyCountry, companyNameForInvoice),
    [companyCountry, companyNameForInvoice]
  );

  const formikTaxBilling = useFormik({
    initialValues: taxBillingDefaultValues(),
    validationSchema: taxBillingSchema,
    onSubmit: onTaxBillingSubmit,
  });

  const {
    errors: errorsTaxBilling,
    touched: touchedTaxBilling,
    values: valuesTaxBilling,
    handleSubmit: handleSubmitTaxBilling,
    isSubmitting: isSubmittingTaxBilling,
    getFieldProps: getFieldPropsTaxBilling,
    resetForm: resetFormTaxBilling,
    setFieldValue: setFieldValueTaxBilling,
  } = formikTaxBilling;

  const onThirdPartySubmit = async (thirdPartyData, { setSubmitting }) => {
    const sanitizeData = sanitizeValues(thirdPartyData);
    
    // Add method_name based on current payment gateway
    sanitizeData.method_name = currentPaymentGateway;
    
    const mutateObject = {};

    if (!isCreateCompany && !is.nullOrUndefined(companyId)) {
      mutateObject.method = "PUT";
      mutateObject.url = getCompanyUrlId(companyId);
    } else {
      dispatch(
        setAlert({
          open: true,
          message: `${COMMONTEXT.REGISTRATION_REQUIRED_MESSAGE} Third Party Settings.`,
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { thirdPartySettings: sanitizeData },
      },
      { onSuccess: onUpdatedSuccess }
    );

    setSubmitting(false);
  };

  const formikThirdParty = useFormik({
    initialValues: thirdPartySettingsDefaultValues(),
    validationSchema: thirdPartyValidationSchema,
    onSubmit: onThirdPartySubmit,
  });

  const {
    errors: errorsThirdParty,
    touched: touchedThirdParty,
    values: valuesThirdParty,
    handleSubmit: handleSubmitThirdParty,
    isSubmitting: isSubmittingThirdParty,
    getFieldProps: getFieldPropsThirdParty,
    resetForm: resetFormThirdParty,
    setFieldValue: setFieldValueThirdParty,
  } = formikThirdParty;

  // Get current payment gateway name (after formik initialization)
  const getCurrentPaymentGatewayName = () => {
    if (gatewayList.length > 0 && valuesComapnyInfo.paymentGatewayId) {
      const gateway = gatewayList.find(g => g.id === Number(valuesComapnyInfo.paymentGatewayId));
      return gateway?.name || "";
    }
    return "";
  };

  const currentPaymentGateway = getCurrentPaymentGatewayName();

  const onWhitelabelInfoSubmit = async (whitelabelData, { setSubmitting }) => {
    const sanitizeData = sanitizeValues(whitelabelData);
    const mutateObject = {};
    if (!isCreateCompany && !is.nullOrUndefined(companyId)) {
      mutateObject.method = "PUT";
      mutateObject.url = getCompanyUrlId(companyId);
    } else {
      dispatch(
        setAlert({
          open: true,
          message: `${COMMONTEXT.REGISTRATION_REQUIRED_MESSAGE} Branding & Customization.`,
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { whitelabelInfo: sanitizeData.whitelabelInfo },
      },
      { onSuccess: onUpdatedSuccess }
    );

    setSubmitting(false);
  };

  const formikWhitelabelInfo = useFormik({
    initialValues: whitelabelInfoDefaultValues(),
    validationSchema: whitelabelInfoValidationSchema,
    onSubmit: onWhitelabelInfoSubmit,
  });

  const {
    errors: errorsWhitelabelInfo,
    touched: touchedWhitelabelInfo,
    values: valuesWhitelabelInfo,
    handleSubmit: handleSubmitWhitelabelInfo,
    isSubmitting: isSubmittingWhitelabelInfo,
    getFieldProps: getFieldPropsWhitelabelInfo,
    resetForm: resetFormWhitelabelInfo,
    setFieldValue: setFieldValueWhitelabelInfo,
  } = formikWhitelabelInfo;

  const onAdvancedSettingsSubmit = async (
    advancedSettingsData,
    { setSubmitting }
  ) => {
    const sanitizeData = sanitizeValues(advancedSettingsData);
    const mutateObject = {};

    if (!isCreateCompany && !is.nullOrUndefined(companyId)) {
      const { erp_company_id, erp_company_name } =
        sanitizeData.advance_settings;
      const [a, b] = erp_company_id ? erp_company_id.split(":") : [null, null];
      mutateObject.method = "PUT";
      mutateObject.url = getCompanyUrlId(companyId);
      sanitizeData.advance_settings.erp_company_name = b || erp_company_name;
      sanitizeData.advance_settings.erp_company_id = a;
    } else {
      dispatch(
        setAlert({
          open: true,
          message: `${COMMONTEXT.REGISTRATION_REQUIRED_MESSAGE} Advanced Settings.`,
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { advancedSettings: sanitizeData }, // key sent to backend
      },
      { onSuccess: onUpdatedSuccess }
    );

    setSubmitting(false);
  };

  const formikAdvancedSettings = useFormik({
    initialValues: advancedSettingsDefaultValue(),
    onSubmit: onAdvancedSettingsSubmit,
  });

  const {
    values: valuesAdvancedSettings,
    handleSubmit: handleSubmitAdvancedSettings,
    isSubmitting: isSubmittingAdvancedSettings,
    getFieldProps: getFieldPropsAdvancedSettings,
    resetForm: resetFormAdvancedSettings,
    setFieldValue: setFieldValueAdvancedSettings,
  } = formikAdvancedSettings;

  const onProductCatalogSubmit = async (
    productCatalogData,
    { setSubmitting }
  ) => {
    const sanitizeData = sanitizeValues(rows);
    const mutateObject = {};

    if (!isCreateCompany && !is.nullOrUndefined(companyId)) {
      mutateObject.method = "PUT";
      mutateObject.url = getCompanyUrlId(companyId);
    } else {
      dispatch(
        setAlert({
          open: true,
          message: `${COMMONTEXT.REGISTRATION_REQUIRED_MESSAGE} Product Catalog.`,
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { productCatalog: sanitizeData }, // send only the required section
      },
      { onSuccess: onUpdatedSuccess }
    );

    setSubmitting(false);
  };

  const formikProductCatalog = useFormik({
    initialValues: productCatalogDefaultValue(),
    onSubmit: onProductCatalogSubmit,
  });

  const {
    handleSubmit: handleSubmitProductCatalog,
    isSubmitting: isSubmittingProductCatalog,
    resetForm: resetFormProductCatalog,
  } = formikProductCatalog;

  const onIntlSubmit = async (intlData, { setSubmitting }) => {
    const { localeFromattingSetting } = sanitizeValues(intlData);
    const mutateObject = {};

    if (!isCreateCompany && !is.nullOrUndefined(companyId)) {
      mutateObject.method = "PUT";
      mutateObject.url = getCompanyUrlId(companyId);
    } else {
      dispatch(
        setAlert({
          open: true,
          message: `${COMMONTEXT.REGISTRATION_REQUIRED_MESSAGE} Internationalization Settings.`,
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { localeFromattingSetting: localeFromattingSetting },
      },
      { onSuccess: onUpdatedSuccess }
    );

    setSubmitting(false);
  };

  const formikIntl = useFormik({
    initialValues: InternationalizationDefaultValues(),
    onSubmit: onIntlSubmit,
  });

  const {
    values: valuesIntl,
    handleSubmit: handleSubmitIntl,
    isSubmitting: isSubmittingIntl,
    getFieldProps: getFieldPropsIntl,
    resetForm: resetFormIntl,
  } = formikIntl;

  const onCommSubmit = async (communicationData, { setSubmitting }) => {
    const sanitizeData = sanitizeValues(communicationData);
    const mutateObject = {};
    if (!isCreateCompany && !is.nullOrUndefined(companyId)) {
      mutateObject.method = "PUT";
      mutateObject.url = getCompanyUrlId(companyId);
      sanitizeData.communication.erp_company_id =
        valuesAdvancedSettings.advance_settings.erp_company_id;
    } else {
      dispatch(
        setAlert({
          open: true,
          message: `${COMMONTEXT.REGISTRATION_REQUIRED_MESSAGE} Communication Settings.`,
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }
    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { communication: sanitizeData.communication }, // key sent to backend
      },
      { onSuccess: onUpdatedSuccess }
    );
    setSubmitting(false);
  };

  const formikCommunicate = useFormik({
    initialValues: communicationDefaultValue(),
    validationSchema: communicationValidationSchema,
    onSubmit: onCommSubmit,
  });

  const {
    errors: errorsCommunicate,
    touched: touchedCommunicate,
    values: valuesCommunicate,
    handleSubmit: handleSubmitCommunicate,
    isSubmitting: isSubmittingCommunicate,
    getFieldProps: getFieldPropsCommunicate,
    setFieldValue: setFieldValueCommSettings,
    resetForm: resetFormCommSettings,
  } = formikCommunicate;

  // Payout Schedule formik
  const onPayoutScheduleSubmit = async (scheduleData, { setSubmitting }) => {
    const sanitizeData = sanitizeValues(scheduleData);
    const mutateObject = {};

    if (!isCreateCompany && !is.nullOrUndefined(companyId)) {
      mutateObject.method = "PUT";
      mutateObject.url = getCompanyUrlId(companyId);
    } else {
      dispatch(
        setAlert({
          open: true,
          message: `${COMMONTEXT.REGISTRATION_REQUIRED_MESSAGE} Payout Schedule Settings.`,
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }
    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { payoutSchedule: sanitizeData },
      },
      { onSuccess: onUpdatedSuccess }
    );
    setSubmitting(false);
  };

  const formikPayoutSchedule = useFormik({
    initialValues: payoutScheduleDefaultValues(),
    validationSchema: payoutScheduleValidationSchema,
    onSubmit: onPayoutScheduleSubmit,
  });

  const {
    errors: errorsPayoutSchedule,
    touched: touchedPayoutSchedule,
    values: valuesPayoutSchedule,
    handleSubmit: handleSubmitPayoutSchedule,
    isSubmitting: isSubmittingPayoutSchedule,
    getFieldProps: getFieldPropsPayoutSchedule,
    setFieldValue: setFieldValuePayoutSchedule,
    resetForm: resetFormPayoutSchedule,
  } = formikPayoutSchedule;

  const onUpdatedSuccess = () => {
    window.location.reload();
  };

  useEffect(() => {
    const catalog = company?.productCatalog;
    if (!(catalog && catalog.length)) {
      const withDefaults =
        product?.map((item) => ({
          ...item,
          access_type: item?.access_type === 0 ? 0 : 2,
          is_visible: 1,
        })) || [];

      setRows(withDefaults);
    } else if (Array.isArray(catalog)) {
      const withDefaults = catalog.map((item) => ({ ...item }));
      setRows(withDefaults);
    }
  }, [isCreateCompany, product, company, isFetching]);

  const handleRowEdit = (newRow) => {
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === newRow.id ? newRow : row))
    );
    return newRow;
  };

  const onSuccess = () => navigate("/company");

  const onCurrencySelection = useCallback(
    (data) => {
      let currency = data;
      if (typeof data === "object" && data !== null && !Array.isArray(data)) {
        currency = data.target.value;
        setFieldValueComapnyInfo("paymentCurrency", currency);
        setFieldValueComapnyInfo("paymentGatewayId", 0);
      }

      //set the taxLable
      if (currencies && currencies.length) {
        const taxLable = currencies.filter(
          (lable) => lable.id === Number(currency)
        );
        setTaxLabel(taxLable[0]?.tax_type);
      }

      const mutateObject = {
        url: getPaymentGateway(currency),
      };
      availablePaymentGatewayList(mutateObject, {
        onSuccess: (data) => {
          const { data: list } = data;
          if (!is.empty(list)) {
            const finalList = [{ id: 0, name: "None" }, ...list];
            setGatewayList(finalList);
          }
        },
      });
    },
    [availablePaymentGatewayList, setFieldValueComapnyInfo, currencies]
  );

  const handleImageUpload = (e, imgType) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    if (imgType === "terms_n_condition_url") {
      if (!isValidFileType(file.type)) {
        dispatch(
          setAlert({
            open: true,
            message: `Only PDF files are allowed.`,
            severity: enums.SEVERITY.ERROR,
          })
        );
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        dispatch(
          setAlert({
            open: true,
            message: `File size exceeds ${MAX_FILE_SIZE_MB} MB.`,
            severity: enums.SEVERITY.ERROR,
          })
        );
        return;
      }
    }

    if (imgType !== "terms_n_condition_url") {
      // Validate file type
      if (!isValidImageType(file.type)) {
        dispatch(
          setAlert({
            open: true,
            message: `Only JPEG, JPG, PNG, WEBP or SVG images are allowed.`,
            severity: enums.SEVERITY.ERROR,
          })
        );
        return;
      }

      // Validate file size
      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        dispatch(
          setAlert({
            open: true,
            message: `File size exceeds ${MAX_IMAGE_SIZE_MB} MB.`,
            severity: enums.SEVERITY.ERROR,
          })
        );
        return;
      }
    }

    const updatedImagePreview = {
      ...imagePreview,
      open: true,
      value: file,
      imgType,
    };
    setImagePreview(updatedImagePreview);
  };

  const uploadImageHandler = async (file, imgType) => {
    if (imgType === "url") {
      setIsUploadingCompanyLogo(true);
    } else if (imgType === "map_marker_active") {
      setIsUploadingMapMarkerActive(true);
    } else if (imgType === "map_marker_inUse") {
      setIsUploadingMapMarkerInUse(true);
    } else if (imgType === "map_marker_inactive") {
      setIsUploadingMapMarkerInactive(true);
    } else if (imgType === "terms_n_condition_url") {
      setIsUploadingTermsNcondition(true);
    }
    imgUpload.mutate(
      {
        image: file,
        folder:
          imgType === "url"
            ? "logo"
            : imgType === "terms_n_condition_url"
            ? "terms_n_condition"
            : "map-marker",
      },
      {
        onSuccess: (response) => {
          if (imgType === "url") {
            setIsUploadingCompanyLogo(false);
          } else if (imgType === "map_marker_active") {
            setIsUploadingMapMarkerActive(false);
          } else if (imgType === "map_marker_inUse") {
            setIsUploadingMapMarkerInUse(false);
          } else if (imgType === "map_marker_inactive") {
            setIsUploadingMapMarkerInactive(false);
          } else if (imgType === "terms_n_condition_url") {
            setIsUploadingTermsNcondition(false);
            setTnCdata(response.data.data.url);
          }
          setImageUploadPending(false);
          if (imgType === "terms_n_condition_url") {
            setFieldValueWhitelabelInfo(`whitelabelInfo[${imgType}]`, response.data.data.url);
          } else {
            const uploadedAttachmentObj = {
              ...valuesWhitelabelInfo.whitelabelInfo,
              [imgType]: response.data.data.url,
            };
            setFieldValueWhitelabelInfo(
              `whitelabelInfo[${imgType}]`,
              uploadedAttachmentObj[imgType]
            );
          }
        },
      }
    );
    closePreviewModal();
  };

  const cancelImageUpload = (imgType) => {
    setFieldValueWhitelabelInfo(`whitelabelInfo[${imgType}]`, "");
    closePreviewModal();
  };

  const closePreviewModal = () => {
    setImagePreview({ ...imagePreview, open: false });
  };

  const [imagePreview, setImagePreview] = useState({
    open: false,
    value: "",
    imgType: "",
    imagePreviewBtns: [
      {
        btnLabel: "Upload",
        action: uploadImageHandler,
        params: true,
      },
      {
        btnLabel: "Cancel",
        action: cancelImageUpload,
        params: true,
      },
    ],
  });

  useEffect(() => {
    if (company) {
      const {
        name,
        subscriptionId,
        emailId,
        address,
        rootUser,
        firstName,
        lastName,
        paymentConfirmationNo,
        taxPercent,
        transactionPercent,
        active,
        minimun_balance,
        defaultPriceGroup,
        charger_configuration_url,
        paymentGatewayId,
        smsGatewayId,
        paymentCurrency,
        whitelabelInfo,
        advance_settings,
        taxBillingConfig,
        is_active,
        public_key,
        secret_key,
        merchant_id,
        webhook_key,
        config_json,
        productCatalog,
        localeFromattingSetting,
        communication,
        dayOfWeek,
        daysOfWeek,
        nthOccurrence,
        dayOfMonth,
        daysOfMonth,
        frequencyType,
        payoutActive,
        lastRunAt,
        lastRunStatus,
        ...rest
      } = company;
      const { zipcode, lat, lng } = address;
      if (whitelabelInfo.terms_n_condition_url) {
        setTnCdata(whitelabelInfo.terms_n_condition_url);
      }

      const {
        url,
        map_marker_active,
        map_marker_inUse,
        map_marker_inactive,
        whitelabel_provider,
        terms_n_condition_url,
        show_all_chargers,
        webapp_url,
      } = whitelabelInfo;

      const [rawProtocol, restChargerConfigurationUrl = ''] = (charger_configuration_url ?? '').split('://');
      const protocol = rawProtocol ? `${rawProtocol}://` : 'ws://';

      const companyInfoValues = {
        name,
        subscriptionId: subscriptionId,
        emailId,
        ...address,
        zipCode: zipcode,
        locationLatitude: lat,
        locationLongitude: lng,
        active: true,
        paymentConfirmationNo: "",
        rootUser,
        firstName,
        lastName,
        paymentConfirmationNo,
        active,
        paymentCurrency,
        paymentGatewayId,
        transactionPercent,
        taxPercent,
        minimun_balance,
        defaultPriceGroup,
        smsGatewayId,
        protocol,
        charger_configuration_url: restChargerConfigurationUrl
      };

      const thirdPartySettingsValue = {
        is_active,
        public_key,
        secret_key,
        merchant_id,
        webhook_key,
        config_json,
      };

      const whitelabelInfoValues = {
        whitelabelInfo: {
          url,
          whitelabel_provider,
          map_marker_active,
          map_marker_inUse,
          map_marker_inactive,
          terms_n_condition_url,
          show_all_chargers,
          webapp_url: webapp_url ?? `${WEB_APP_ENDPOINT}/charger/`,
        },
      };

      const {
        erp_company_id,
        erp_company_name,
        enforce_crm_user_only,
        restrict_charger_visibility,
        allow_other_cpo_chargers,
        enable_license_payment,
        allow_manual_balance_edit,
      } = advance_settings;

      const advanceSettingsValue = {
        advance_settings: {
          erp_company_id,
          erp_company_name,
          enforce_crm_user_only,
          restrict_charger_visibility,
          allow_other_cpo_chargers,
          enable_license_payment,
          allow_manual_balance_edit,
        },
      };

      const payoutscheduleValues = {
        dayOfWeek,
        daysOfWeek,
        nthOccurrence,
        dayOfMonth,
        daysOfMonth,
        frequencyType,
        isActive: payoutActive,
        lastRunAt,
        lastRunStatus,
      }

      resetFormComapnyInfo({ values: companyInfoValues });
      const taxBillingDefaults = taxBillingDefaultValues().taxBillingConfig;
      resetFormTaxBilling({
        values: {
          taxBillingConfig: {
            ...taxBillingDefaults,
            ...taxBillingConfig,
            invoiceNumbering: {
              ...taxBillingDefaults.invoiceNumbering,
              ...(taxBillingConfig?.invoiceNumbering || {}),
            },
            invoiceReceiptSetting: {
              ...taxBillingDefaults.invoiceReceiptSetting,
              ...(taxBillingConfig?.invoiceReceiptSetting || {}),
            },
          },
        },
      });
      resetFormIntl({
        values: { localeFromattingSetting: localeFromattingSetting },
      });
      resetFormThirdParty({ values: thirdPartySettingsValue });
      resetFormWhitelabelInfo({ values: whitelabelInfoValues });
      resetFormAdvancedSettings({ values: advanceSettingsValue });
      resetFormProductCatalog({ values: productCatalog });
      resetFormCommSettings({ values: { communication } });
      resetFormPayoutSchedule({ values: payoutscheduleValues });

      if (paymentCurrency) {
        onCurrencySelection(paymentCurrency);
      }
    }
  }, [
    company,
    resetFormComapnyInfo,
    resetFormTaxBilling,
    resetFormThirdParty,
    resetFormAdvancedSettings,
    resetFormWhitelabelInfo,
    resetFormProductCatalog,
    resetFormIntl,
    onCurrencySelection,
    resetFormCommSettings,
    resetFormPayoutSchedule
  ]);
  if (isFetching) {
    return <Loader />;
  }

  if (isFetchingProduct) return <Loader />;

  if (!isCreateCompany && is.empty(company)) {
    navigate("/company");
    return null;
  }
  return (
    <>
      <CommonAccordion
        title="Company Info"
        defaultExpanded
        onChange={handleAccordionChange("companyInfo")}
        expanded={expandedAccordion === "companyInfo"}
      >
        <Suspense fallback={<Loader />}>
          <FormikProvider value={formikComapnyInfo}>
            <Form
              autoComplete="off"
              id="company"
              noValidate
              onSubmit={handleSubmitComapnyInfo}
            >
              <Card sx={{ p: 2 }}>
                <CompanyBasicInfoSection
                  values={valuesComapnyInfo}
                  touched={touchedComapnyInfo}
                  errors={errorsComapnyInfo}
                  getFieldProps={getFieldPropsComapnyInfo}
                  currencies={currencies}
                  smsGatewayList={smsGatewayList}
                  gatewayList={gatewayList}
                  onCurrencySelection={onCurrencySelection}
                  setFieldValue={setFieldValueComapnyInfo}
                  taxLabel={taxLabel}
                  isCreateCompany={isCreateCompany}
                />
                <CompanyAddressSection
                  getFieldProps={getFieldPropsComapnyInfo}
                  setFieldValue={setFieldValueComapnyInfo}
                  values={valuesComapnyInfo}
                />
                <CompanyInfoAndCommentsSection
                  values={valuesComapnyInfo}
                  touched={touchedComapnyInfo}
                  errors={errorsComapnyInfo}
                  getFieldProps={getFieldPropsComapnyInfo}
                  setFieldValue={setFieldValueComapnyInfo}
                  isCreateCompany={isCreateCompany}
                />
                {canEdit && (
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ ml: 3, justifyContent: "flex-end" }}
                  >
                    <LoadingButton
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={isSubmittingComapnyInfo || isLoading}
                    >
                      Save
                    </LoadingButton>
                  </Stack>
                )}
              </Card>
            </Form>
          </FormikProvider>
        </Suspense>
      </CommonAccordion>
      {!isCreateCompany && (
        <>
          <CommonAccordion
            title="Financial Settings"
            onChange={handleAccordionChange("financialSettings")}
            expanded={expandedAccordion === "financialSettings"}
          >
            {expandedAccordion === "financialSettings" && (
              <Suspense fallback={<Loader />}>
                <FormikProvider value={formikTaxBilling}>
                  <Form
                    autoComplete="off"
                    id="company"
                    noValidate
                    onSubmit={handleSubmitTaxBilling}
                  >
                    <Card sx={{ p: 2 }}>
                      <CompanyFinancialSettingsSection
                        values={valuesTaxBilling}
                        touched={touchedTaxBilling}
                        errors={errorsTaxBilling}
                        getFieldProps={getFieldPropsTaxBilling}
                        setFieldValue={setFieldValueTaxBilling}
                        TaxRegimeType={TaxRegimeType}
                        TaxIDFieldLabel={TaxIDFieldLabel}
                        InvoiceNumberSchema={InvoiceNumberSchema}
                        companyName={valuesComapnyInfo?.name}
                        country={companyCountry}
                      />
                      {canEdit && (
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{ ml: 3, justifyContent: "flex-end" }}
                        >
                          <LoadingButton
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isSubmittingTaxBilling || isLoading}
                          >
                            Save
                          </LoadingButton>
                        </Stack>
                      )}
                    </Card>
                  </Form>
                </FormikProvider>
              </Suspense>
            )}
          </CommonAccordion>
          <CommonAccordion
            title="Internationalization Settings"
            onChange={handleAccordionChange("intlSettings")}
            expanded={expandedAccordion === "intlSettings"}
          >
            {expandedAccordion === "intlSettings" && (
              <Suspense fallback={<Loader />}>
                <FormikProvider value={formikIntl}>
                  <Form
                    autoComplete="off"
                    id="intl-form"
                    noValidate
                    onSubmit={handleSubmitIntl}
                  >
                    <Card sx={{ p: 2 }}>
                      <CompanyInternationation
                        values={valuesIntl}
                        getFieldProps={getFieldPropsIntl}
                        localeCode={localeCode}
                        timeZone={timeZone}
                        dateFormat={dateFormat}
                        addressFormat={addressFormat}
                        numberFormat={numberFormat}
                      />
                      {canEdit && (
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{ ml: 3, justifyContent: "flex-end", mt: 3 }}
                        >
                          <LoadingButton
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isSubmittingIntl || isLoading}
                          >
                            Save
                          </LoadingButton>
                        </Stack>
                      )}
                    </Card>
                  </Form>
                </FormikProvider>
              </Suspense>
            )}
          </CommonAccordion>
          <CommonAccordion
            title="Third Party Settings"
            onChange={handleAccordionChange("thirdPartySettings")}
            expanded={expandedAccordion === "thirdPartySettings"}
          >
            {expandedAccordion === "thirdPartySettings" && (
              <Suspense fallback={<Loader />}>
                <FormikProvider value={formikThirdParty}>
                  <Form
                    autoComplete="off"
                    id="company"
                    noValidate
                    onSubmit={handleSubmitThirdParty}
                  >
                    <Card sx={{ p: 2 }}>
                      <CompanyPaymentConfigSection
                        values={valuesThirdParty}
                        touched={touchedThirdParty}
                        errors={errorsThirdParty}
                        getFieldProps={getFieldPropsThirdParty}
                        setFieldValue={setFieldValueThirdParty}
                        currentPaymentGateway={currentPaymentGateway}
                      />
                      {canEdit && (
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{ ml: 3, justifyContent: "flex-end" }}
                        >
                          <LoadingButton
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isSubmittingThirdParty || isLoading}
                          >
                            Save
                          </LoadingButton>
                        </Stack>
                      )}
                    </Card>
                  </Form>
                </FormikProvider>
              </Suspense>
            )}
          </CommonAccordion>
          <CommonAccordion
            title="Branding & Customization"
            onChange={handleAccordionChange("bAndC")}
            expanded={expandedAccordion === "bAndC"}
          >
            {expandedAccordion === "bAndC" && (
              <Suspense fallback={<Loader />}>
                <FormikProvider value={formikWhitelabelInfo}>
                  <Form
                    autoComplete="off"
                    id="company"
                    noValidate
                    onSubmit={handleSubmitWhitelabelInfo}
                  >
                    <Card sx={{ p: 2 }}>
                      <CompanyWhitelabelSection
                        values={valuesWhitelabelInfo}
                        touched={touchedWhitelabelInfo}
                        errors={errorsWhitelabelInfo}
                        getFieldProps={getFieldPropsWhitelabelInfo}
                        setFieldValue={setFieldValueWhitelabelInfo}
                        imageUploadPending={imageUploadPending}
                        imageUploadInputRef={imageUploadInputRef}
                        mapMarkerActiveInputRef={mapMarkerActiveInputRef}
                        mapMarkerInUseInputRef={mapMarkerInUseInputRef}
                        mapMarkerInactiveInputRef={mapMarkerInactiveInputRef}
                        termsNconditionUrl={termsNconditionUrl}
                        isUploadingCompanyLogo={isUploadingCompanyLogo}
                        isUploadingMapMarkerActive={isUploadingMapMarkerActive}
                        isUploadingMapMarkerInUse={isUploadingMapMarkerInUse}
                        isUploadingMapMarkerInactive={
                          isUploadingMapMarkerInactive
                        }
                        isUploadingTermsNcondition={isUploadingTermsNcondition}
                        handleImageUpload={handleImageUpload}
                        setPhotoModal={setPhotoModal}
                        photoModal={photoModal}
                        setImagePreview={setImagePreview}
                        imagePreview={imagePreview}
                        ImgPreviewModal={ImgPreviewModal}
                        Dialog={Dialog}
                        DialogContent={DialogContent}
                        IconButton={IconButton}
                        GridCloseIcon={GridCloseIcon}
                        Worker={Worker}
                        Viewer={Viewer}
                        tnCdata={tnCdata}
                        canEdit={canEdit}
                      />
                      {canEdit && (
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{ ml: 3, justifyContent: "flex-end" }}
                        >
                          <LoadingButton
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isSubmittingWhitelabelInfo || isLoading}
                          >
                            Save
                          </LoadingButton>
                        </Stack>
                      )}
                    </Card>
                  </Form>
                </FormikProvider>
              </Suspense>
            )}
          </CommonAccordion>
          <CommonAccordion
            title="Advanced Settings"
            onChange={handleAccordionChange("advanceSettings")}
            expanded={expandedAccordion === "advanceSettings"}
          >
            <Suspense fallback={<Loader />}>
              <FormikProvider value={formikAdvancedSettings}>
                <Form
                  autoComplete="off"
                  id="company"
                  noValidate
                  onSubmit={handleSubmitAdvancedSettings}
                >
                  <Card sx={{ p: 2 }}>
                    <AdvanceSettingsSection
                      getFieldProps={getFieldPropsAdvancedSettings}
                      values={valuesAdvancedSettings}
                      setFieldValue={setFieldValueAdvancedSettings}
                      isCreateCompany={isCreateCompany}
                      isSubmitting={isSubmittingAdvancedSettings}
                    />
                    {canEdit && (
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ ml: 3, justifyContent: "flex-end" }}
                      >
                        <LoadingButton
                          size="large"
                          type="submit"
                          variant="contained"
                          loading={isSubmittingAdvancedSettings || isLoading}
                        >
                          Save
                        </LoadingButton>
                      </Stack>
                    )}
                  </Card>
                </Form>
              </FormikProvider>
            </Suspense>
          </CommonAccordion>
          <CompanyFeatureEntitlements
            companyId={companyId}
            expanded={expandedAccordion === "featureEntitlements"}
            onAccordionChange={handleAccordionChange("featureEntitlements")}
          />
        </>
      )}
      {!isCreateCompany && (
        <CommonAccordion
          title="Payout Schedule"
          onChange={handleAccordionChange("payoutSchedule")}
          expanded={expandedAccordion === "payoutSchedule"}
        >
          {expandedAccordion === "payoutSchedule" && (
            <Suspense fallback={<Loader />}>
              <FormikProvider value={formikPayoutSchedule}>
                <Form
                  autoComplete="off"
                  id="payout-schedule"
                  noValidate
                  onSubmit={handleSubmitPayoutSchedule}
                >
                  <Card sx={{ p: 2 }}>
                    <PayoutScheduleSection
                      values={valuesPayoutSchedule}
                      touched={touchedPayoutSchedule}
                      errors={errorsPayoutSchedule}
                      getFieldProps={getFieldPropsPayoutSchedule}
                      setFieldValue={setFieldValuePayoutSchedule}
                    />
                    {canEdit && (
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ ml: 3, justifyContent: "flex-end", mt: 2 }}
                      >
                        <LoadingButton
                          size="large"
                          type="submit"
                          variant="contained"
                          loading={
                            isSubmittingPayoutSchedule
                          }
                        >
                          Save
                        </LoadingButton>
                      </Stack>
                    )}
                  </Card>
                </Form>
              </FormikProvider>
            </Suspense>
          )}
        </CommonAccordion>
      )}
      {!isCreateCompany && company?.advance_settings?.erp_company_id && (
        <CommonAccordion
          title="Communication Settings"
          onChange={handleAccordionChange("commSettings")}
          expanded={expandedAccordion === "commSettings"}
        >
          {expandedAccordion === "commSettings" && (
            <Suspense fallback={<Loader />}>
              <FormikProvider value={formikCommunicate}>
                <Form
                  autoComplete="off"
                  id="communication"
                  noValidate
                  onSubmit={handleSubmitCommunicate}
                >
                  <Card sx={{ p: 2 }}>
                    <Communicate
                      values={valuesCommunicate}
                      touched={touchedCommunicate}
                      errors={errorsCommunicate}
                      getFieldProps={getFieldPropsCommunicate}
                      setFieldValue={setFieldValueCommSettings}
                      isCreateCompany={isCreateCompany}
                      isSubmitting={isSubmittingCommunicate}
                      advanceSettingValue={valuesAdvancedSettings}
                    />
                    {canEdit && (
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ ml: 3, justifyContent: "flex-end" }}
                      >
                        <LoadingButton
                          size="large"
                          type="submit"
                          variant="contained"
                          loading={isSubmittingCommunicate || isLoading}
                        >
                          Save
                        </LoadingButton>
                      </Stack>
                    )}
                  </Card>
                </Form>
              </FormikProvider>
            </Suspense>
          )}
        </CommonAccordion>
      )}
      {!isCreateCompany && (
        <CommonAccordion
          title="Product Catalog"
          onChange={handleAccordionChange("catalog")}
          expanded={expandedAccordion === "catalog"}
        >
          {expandedAccordion === "catalog" && (
            <Suspense fallback={<Loader />}>
              <FormikProvider value={formikProductCatalog}>
                <Form
                  autoComplete="off"
                  id="product-catalog"
                  noValidate
                  onSubmit={handleSubmitProductCatalog}
                >
                  <Card sx={{ p: 2 }}>
                    <BaseTable
                      rows={rows}
                      columns={ProductListColumns(handleRowEdit)}
                      isFetching={isFetching}
                      isCellEditable={(params) => {
                        if (params.field === "is_visible") {
                          return params.row.access_type === 0;
                        }
                        return params.row.access_type !== 0;
                      }}
                      processRowUpdate={handleRowEdit}
                      experimentalFeatures={{ newEditingApi: true }}
                      pageSize={10}
                    />
                    {canEdit && (
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ ml: 3, justifyContent: "flex-end", mt: 2 }}
                      >
                        <LoadingButton
                          size="large"
                          type="submit"
                          variant="contained"
                          loading={isSubmittingProductCatalog || isLoading}
                        >
                          Save
                        </LoadingButton>
                      </Stack>
                    )}
                  </Card>
                </Form>
              </FormikProvider>
            </Suspense>
          )}
        </CommonAccordion>
      )}
    </>
  );
};
